/**
 * GTM GA4 E-commerce Event Tracking
 * Handles GA4 e-commerce events based on window.gtmConfig flags
 *
 * Supported Events:
 * 
 * - viewed_category: Collection page views
 * - view_item: Product page views
 * - view_item_list: Collection/category page views
 * - add_to_cart: Add to cart actions
 * - view_cart: Cart page and cart drawer views
 * - view_search_result: Native and Prefixbox search result views
 * - cart_error: Cart-level and line-level errors (cart page + mini-cart drawer)
 * - error_404: 404 page loads (page_location, page_path, page_referrer, page_title)
 * - page_load_time / server_response_time: Navigation Timing (ms) per page (TWS-1048)
 * - purchase: Order completions
 * - userId / uid: Attached on every named event when logged-in AMS identity is available (TWS-931)
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

// Check if GTM is enabled
// @ts-ignore - GTM config is injected by Liquid template
if (!window.gtmConfig || !window.gtmConfig.enabled) {
  console.log('GTM events disabled');
} else {
  /**
   * Debug logger - only logs when debug mode is enabled
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  function gtmDebugLog(event, data) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.debugMode) {
      console.log(`[GTM Debug] ${event}:`, data);
    }
  }

  /**
   * Clear previous ecommerce data before pushing new event
   * Recommended by Google to avoid data pollution
   */
  function clearEcommerceData() {
    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push({ ecommerce: null });
  }

  /**
   * Logged-in AMS identity (TWS-931). Guests / incomplete profiles: omit fields.
   * @returns {{ userId: string, uid: { zipcode: string, email: { hashed: string } } } | null}
   */
  function getUserIdentityFields() {
    // @ts-ignore - set by snippets/gtm-head.liquid
    const identity = window.gtmUserIdentity;
    if (!identity?.userId || !identity?.uid?.email?.hashed) return null;
    return {
      userId: String(identity.userId),
      uid: {
        zipcode: identity.uid.zipcode != null ? String(identity.uid.zipcode) : '',
        email: {
          hashed: String(identity.uid.email.hashed),
        },
      },
    };
  }

  /**
   * Push a named GTM event with userId/uid attached when available.
   * Awaits SHA-256 readiness so early page-view events still get identity.
   * @param {Object} eventData
   * @returns {Promise<Object>}
   */
  async function pushGtmEvent(eventData) {
    // @ts-ignore
    if (window.gtmUserIdentityReady) {
      try {
        // @ts-ignore
        await window.gtmUserIdentityReady;
      } catch (e) {
        /* identity optional for guests / hash failures */
      }
    }

    const identity = getUserIdentityFields();
    const payload = identity ? { ...eventData, ...identity } : eventData;
    // @ts-ignore - dataLayer is GTM global variable
    window.dataLayer.push(payload);
    return payload;
  }

  /**
   * Resolve ams_item_id for GA4 items[] — custom.vsfccmaster when set, else product/variant id.
   * @param {Object} item
   * @returns {string}
   */
  function resolveAmsItemId(item) {
    const explicit = item?.ams_item_id ?? item?.vsfccmaster;
    if (explicit != null && String(explicit).trim() !== '') {
      return String(explicit);
    }
    const fallback =
      item?.variant_id ?? item?.masterProductId ?? item?.product_id ?? item?.item_id ?? '';
    return String(fallback);
  }

  /**
   * Resolve the Shopify parent product id for GA4 items[].masterProductId (TWS-1449).
   * Never falls back to item_id / variant_id: a variant id here would split the
   * product-level funnel this field exists to join.
   * @param {Object} item
   * @returns {string} Numeric product id, or '' when unknown.
   */
  function resolveMasterProductId(item) {
    const raw = item?.masterProductId ?? item?.product_id;
    if (raw == null) return '';
    const id = String(raw).trim().replace(/^gid:\/\/shopify\/Product\//i, '');
    return /^\d+$/.test(id) ? id : '';
  }

  /**
   * Item-level masterProductId, omitted when the parent id is unknown.
   * @param {Object} item
   * @returns {{masterProductId?: string}}
   */
  function masterProductIdField(item) {
    const id = resolveMasterProductId(item);
    return id ? { masterProductId: id } : {};
  }

  /**
   * Whether an element is shown to the user (not in a closed dialog / display:none subtree).
   * Mirrors jQuery :visible roughly; used so mini-cart errors are ignored while the drawer is closed.
   * @param {Element} el
   * @returns {boolean}
   */
  function isElementVisible(el) {
    if (!(el instanceof Element)) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }
    const htmlEl = /** @type {HTMLElement} */ (el);
    return Boolean(htmlEl.offsetParent || el.getClientRects?.().length);
  }

  /**
   * Finds a cart line key for GTM "Product Error" prefix (Shopify line item key on row/container).
   * @param {Element} el
   * @returns {string}
   */
  function findCartLineItemKey(el) {
    const row = el.closest('tr[data-key], .cart-page__item[data-key]');
    return row?.getAttribute('data-key')?.trim() || '';
  }

  /**
   * Root node for line-level errors: full cart page list vs mini-cart only, avoids duplicate pushes
   * when both layouts exist in the DOM.
   * @returns {ParentNode | null}
   */
  function getProductErrorScanRoot() {
    // @ts-ignore
    const pageType = window.gtmConfig.pageType;
    if (pageType === 'cart') {
      return document.querySelector('.cart-page__products-list');
    }
    return document.querySelector('cart-drawer-component.cart-drawer');
  }

  /** @type {Record<string, number>} */
  const cartErrorAttemptCounts = {};
  /** @type {Map<string, number>} */
  const cartErrorRecentSignatures = new Map();
  const CART_ERROR_DEDUPE_MS = 2500;
  /** @type {Promise<object|null>|null} */
  let cartSnapshotPromise = null;

  /**
   * Strip HTML and normalize whitespace for error_message.
   * @param {string} raw
   * @returns {string}
   */
  function normalizeCartErrorMessage(raw) {
    const withoutTags = String(raw || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return withoutTags;
  }

  /**
   * @returns {boolean}
   */
  function isCartErrorTrackingEnabled() {
    // @ts-ignore
    return window.gtmConfig.cartError !== false;
  }

  /**
   * @returns {Promise<object|null>}
   */
  function fetchCartSnapshot() {
    if (cartSnapshotPromise) return cartSnapshotPromise;
    const sharedFetch = window.TJC?.fetchCartJson;
    if (typeof sharedFetch === 'function') {
      cartSnapshotPromise = sharedFetch()
        .catch(() => null)
        .finally(() => {
          setTimeout(() => {
            cartSnapshotPromise = null;
          }, 3000);
        });
      return cartSnapshotPromise;
    }
    cartSnapshotPromise = fetch('/cart.js')
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .finally(() => {
        setTimeout(() => {
          cartSnapshotPromise = null;
        }, 3000);
      });
    return cartSnapshotPromise;
  }

  /**
   * @param {object|null} cartApi
   * @returns {{ cart_value: number, currency: string, cart_item_count: number }}
   */
  function getCartMetricsFromSnapshot(cartApi) {
    // @ts-ignore
    const currency = window.gtmConfig.currency || cartApi?.currency || 'GBP';
    if (cartApi && typeof cartApi?.total_price === 'number') {
      return {
        cart_value: Number((cartApi?.total_price / 100).toFixed(2)),
        currency,
        cart_item_count: Number(cartApi?.item_count) || 0,
      };
    }
    // @ts-ignore
    const configCart = window.gtmConfig.cart;
    return {
      cart_value: parseFloat(configCart?.value) || 0,
      currency,
      cart_item_count: Number(configCart?.item_count) || configCart?.items?.length || 0,
    };
  }

  /**
   * @param {Element} el
   * @returns {{
   *   product_id?: string,
   *   variant_id?: string,
   *   product_title?: string,
   *   variant_sku?: string,
   *   ams_item_id?: string,
   *   item_brand?: string,
   *   category?: string,
   *   item_variant?: string,
   *   warranty_selected?: boolean,
   *   articleType?: string,
   *   color?: string,
   *   size?: string,
   *   mainStoneName?: string,
   *   metalType?: string,
   *   inventory_quantity?: number,
   *   requested_quantity?: number,
   *   is_auction?: boolean,
   *   is_live_tv?: boolean
   * }}
   */
  function getLineMetaFromElement(el) {
    const row = el.closest('tr[data-key], .cart-page__item[data-key], .cart-gtm4-row[data-key]');
    if (!(row instanceof HTMLElement)) return {};

    const inventoryRaw = row.dataset.inventoryQuantity;
    const requestedRaw = row.dataset.requestedQuantity;

    return {
      product_id: row.dataset.productId || undefined,
      variant_id: row.dataset.variantId || undefined,
      product_title: row.dataset.productTitle || undefined,
      variant_sku: row.dataset.variantSku || undefined,
      ams_item_id: row.dataset.amsItemId || undefined,
      item_brand: row.dataset.itemBrand || undefined,
      category: row.dataset.itemCategory || undefined,
      item_variant: row.dataset.itemVariant || undefined,
      warranty_selected: row.dataset.warrantySelected === 'true',
      articleType: row.dataset.articleType || undefined,
      color: row.dataset.color || undefined,
      size: row.dataset.size || undefined,
      mainStoneName: row.dataset.mainStoneName || undefined,
      metalType: row.dataset.metalType || undefined,
      inventory_quantity:
        inventoryRaw !== undefined && inventoryRaw !== '' ? Number(inventoryRaw) : undefined,
      requested_quantity:
        requestedRaw !== undefined && requestedRaw !== '' ? Number(requestedRaw) : undefined,
      is_auction: row.dataset.isAuction === 'true',
      is_live_tv: row.dataset.isLiveTv === 'true',
    };
  }

  /**
   * @param {string} message
   * @param {{ is_auction?: boolean, is_live_tv?: boolean }} [lineMeta]
   * @returns {'coupon'|'product_availability'|'stock_limit'|'system'}
   */
  function classifyCartErrorCategory(message, lineMeta = {}) {
    const m = message.toLowerCase();

    if (
      m.includes('discount') ||
      m.includes('promotional') ||
      m.includes('promo code') ||
      m.includes('coupon') ||
      m.includes('voucher')
    ) {
      return 'coupon';
    }

    if (
      m.includes('left in stock') ||
      m.includes('adjust the quantity') ||
      m.includes('only add') ||
      m.includes('maximum quantity') ||
      m.includes('not enough stock') ||
      m.includes('exceeds available')
    ) {
      return 'stock_limit';
    }

    if (
      m.includes('no longer available') ||
      m.includes('sold out') ||
      m.includes('out of stock') ||
      m.includes('remove it to proceed') ||
      m.includes('remove this item') ||
      m.includes('unavailable')
    ) {
      return 'product_availability';
    }

    if (lineMeta.is_auction || lineMeta.is_live_tv) {
      if (m.includes('live tv') || m.includes('auction') || m.includes('no longer')) {
        return 'product_availability';
      }
    }

    if (
      m.includes('technical reason') ||
      m.includes('could not be handled') ||
      m.includes('try again later') ||
      m.includes('refresh and try') ||
      m.includes('could not validate')
    ) {
      return 'system';
    }

    return 'system';
  }

  /**
   * @param {string} message
   * @param {'coupon'|'product_availability'|'stock_limit'|'system'} category
   * @param {object} [ctx]
   * @returns {string}
   */
  function resolveCartErrorCode(message, category, ctx = {}) {
    const m = message.toLowerCase();

    if (category === 'coupon') {
      if (ctx.coupon_reason === 'empty_input') return 'EMPTY_COUPON';
      if (ctx.coupon_reason === 'already_applied') return 'ALREADY_APPLIED';
      if (ctx.coupon_reason === 'expired') return 'EXPIRED_COUPON';
      if (ctx.coupon_reason === 'not_combinable') return 'NOT_COMBINABLE';
      if (ctx.coupon_reason === 'usage_limit') return 'USAGE_LIMIT';
      if (ctx.coupon_reason === 'not_applicable') return 'NOT_APPLICABLE';
      if (m.includes('shipping')) return 'SHIPPING_DISCOUNT_ERROR';
      return 'INVALID_COUPON';
    }

    if (category === 'stock_limit') return 'STOCK_LIMIT';
    if (category === 'product_availability') {
      if (ctx.unavailable_reason === 'auction_ended') return 'AUCTION_ENDED';
      if (ctx.unavailable_reason === 'discontinued') return 'DISCONTINUED';
      if (ctx.unavailable_reason === 'region_restricted') return 'REGION_RESTRICTED';
      if (ctx.unavailable_reason === 'temporarily_unavailable') return 'TEMPORARILY_UNAVAILABLE';
      return 'UNAVAILABLE_LINE_ITEM';
    }

    if (ctx.system_error_code) return String(ctx.system_error_code);
    if (m.includes('budget pay')) return 'BUDGET_PAY_VALIDATION';
    return 'CART_API_FAILURE';
  }

  /**
   * @param {string} message
   * @returns {{ prefix: string, body: string }|null}
   */
  function parsePrefixedLineError(message) {
    const match = String(message).match(/^([^:]{1,64}):\s*(.+)$/);
    if (!match) return null;
    return { prefix: match[1].trim(), body: match[2].trim() };
  }

  /**
   * @param {string} message
   * @returns {{ requested_quantity?: number, available_quantity?: number }}
   */
  function parseStockQuantitiesFromMessage(message) {
    const result = {};
    const availableMatch = message.match(/(\d+)\s+item\(s\)\s+left in stock/i);
    if (availableMatch) {
      result.available_quantity = parseInt(availableMatch[1], 10);
    }
    const onlyAddMatch = message.match(/only add (\d+)/i);
    if (onlyAddMatch) {
      result.available_quantity = parseInt(onlyAddMatch[1], 10);
    }
    return result;
  }

  /**
   * @param {string} message
   * @param {{ is_auction?: boolean, is_live_tv?: boolean }} lineMeta
   * @returns {string|undefined}
   */
  function resolveUnavailableReason(message, lineMeta) {
    const m = message.toLowerCase();
    if (lineMeta.is_live_tv || lineMeta.is_auction || m.includes('live tv') || m.includes('auction')) {
      return 'auction_ended';
    }
    if (m.includes('region') || m.includes('country')) return 'region_restricted';
    if (m.includes('temporarily')) return 'temporarily_unavailable';
    if (m.includes('no longer') || m.includes('unpublished') || m.includes('discontinued')) {
      return 'discontinued';
    }
    return 'temporarily_unavailable';
  }

  /** @type {readonly string[]} */
  const OVERALL_ERROR_MESSAGES = [
    'Hey! looks like there are some items that no longer available to purchase',
    'For technical reasons, your request could not be handled properly at this time',
    'Ah! looks like the current product is no longer available.',
  ];

  /**
   * @param {'coupon'|'product_availability'|'stock_limit'|'system'} error_category
   * @param {string} message
   * @returns {'Overall Error'|'Coupon Error'|'Product Error'}
   */
  function resolveCartErrorName(error_category, message) {
    const normalized = normalizeCartErrorMessage(message);
    if (OVERALL_ERROR_MESSAGES.includes(normalized)) return 'Overall Error';
    if (error_category === 'coupon') return 'Coupon Error';
    if (error_category === 'product_availability' || error_category === 'stock_limit') {
      return 'Product Error';
    }
    return 'Overall Error';
  }

  /**
   * @param {'coupon'|'product_availability'|'stock_limit'|'system'} category
   * @returns {boolean}
   */
  function isRecoverableCartError(category) {
    return category === 'coupon' || category === 'stock_limit';
  }

  /**
   * @param {object} payload
   * @returns {string}
   */
  function buildCartErrorSignature(payload) {
    return [
      payload.error_category,
      payload.error_code,
      payload.error_message,
      payload.variant_id || '',
      payload.coupon_code || '',
    ].join('|');
  }

  /**
   * @param {string} signature
   * @returns {number}
   */
  function nextCartErrorAttempt(signature) {
    cartErrorAttemptCounts[signature] = (cartErrorAttemptCounts[signature] || 0) + 1;
    return cartErrorAttemptCounts[signature];
  }

  /**
   * Push GA4 cart_error to dataLayer.
   * @param {object} input
   * @param {string} [input.error_category]
   * @param {string} [input.error_code]
   * @param {string} input.error_message
   * @param {boolean} [input.is_recoverable]
   * @param {string} [input.coupon_code]
   * @param {string} [input.coupon_reason]
   * @param {string} [input.product_id]
   * @param {string} [input.variant_id]
   * @param {string} [input.product_title]
   * @param {string} [input.unavailable_reason]
   * @param {number} [input.requested_quantity]
   * @param {number} [input.available_quantity]
   * @param {string} [input.system_error_code]
   * @param {string} [input.subsystem]
   * @param {Element} [input.element]
   * @param {number} [input.cart_value]
   * @param {string} [input.currency]
   * @param {number} [input.cart_item_count]
   */
  async function trackCartError(input) {
    if (!isCartErrorTrackingEnabled()) return;

    const errorMessage = normalizeCartErrorMessage(input.error_message);
    if (!errorMessage) return;

    const lineMeta = input.element ? getLineMetaFromElement(input.element) : {};
    const prefixed = parsePrefixedLineError(errorMessage);
    const messageBody = prefixed ? prefixed.body : errorMessage;

    const error_category =
      input.error_category || classifyCartErrorCategory(errorMessage, lineMeta);

    /** @type {Record<string, unknown>} */
    const payload = {
      error_category,
      error_name: input.error_name || resolveCartErrorName(error_category, errorMessage),
      error_message: errorMessage,
    };

    if (error_category === 'coupon') {
      payload.coupon_code = input.coupon_code || '';
      payload.coupon_reason = input.coupon_reason || 'invalid';
    }

    if (error_category === 'product_availability' || error_category === 'stock_limit') {
      const prefixId = prefixed?.prefix || '';
      payload.product_id = input.product_id || lineMeta.product_id || undefined;
      payload.variant_id =
        input.variant_id ||
        lineMeta.variant_id ||
        (/^\d+$/.test(prefixId) ? prefixId : undefined) ||
        (prefixId.startsWith('V') ? prefixId.slice(1) : prefixId || undefined);
      payload.product_title = input.product_title || lineMeta.product_title || undefined;

      if (error_category === 'product_availability') {
        payload.unavailable_reason =
          input.unavailable_reason || resolveUnavailableReason(messageBody, lineMeta);
      }

      if (error_category === 'stock_limit') {
        const parsedQty = parseStockQuantitiesFromMessage(messageBody);
        payload.requested_quantity =
          input.requested_quantity ?? lineMeta.requested_quantity ?? undefined;
        payload.available_quantity =
          input.available_quantity ??
          parsedQty.available_quantity ??
          lineMeta.inventory_quantity ??
          undefined;
      }
    }

    if (error_category === 'system') {
      payload.system_error_code = input.system_error_code || undefined;
      payload.subsystem = input.subsystem || 'unknown';
    }

    payload.error_code =
      input.error_code || resolveCartErrorCode(messageBody, error_category, payload);
    payload.is_recoverable =
      typeof input.is_recoverable === 'boolean'
        ? input.is_recoverable
        : isRecoverableCartError(error_category);

    const cartApi = await fetchCartSnapshot();
    const metrics =
      input.cart_value != null && input.cart_item_count != null
        ? {
            cart_value: input.cart_value,
            currency: input.currency || window.gtmConfig?.currency,
            cart_item_count: input.cart_item_count,
          }
        : getCartMetricsFromSnapshot(cartApi);

    payload.cart_value = metrics.cart_value;
    payload.currency = metrics.currency;
    payload.cart_item_count = metrics.cart_item_count;

    const signature = buildCartErrorSignature(payload);
    const now = Date.now();
    const lastFired = cartErrorRecentSignatures.get(signature);
    if (lastFired && now - lastFired < CART_ERROR_DEDUPE_MS) return;
    cartErrorRecentSignatures.set(signature, now);

    payload.attempt_number = nextCartErrorAttempt(signature);

    const eventData = {
      event: 'cart_error',
      ...payload,
    };

    const cartErrorPayload = await pushGtmEvent(eventData);
    gtmDebugLog('cart_error', cartErrorPayload);
  }

  /**
   * Scans visible cart error UI (fallback when components do not dispatch structured events).
   * @param {ParentNode} [root=document]
   */
  function captureCartErrorsForGA(root = document) {
    if (!isCartErrorTrackingEnabled()) return;

    const scanRoot = root instanceof Document ? document.body : root;
    if (!scanRoot) return;

    scanRoot.querySelectorAll('.message.error-form .message-content, .error-form.message .message-content').forEach((node) => {
      if (!isElementVisible(node)) return;
      const text = normalizeCartErrorMessage(node.textContent || '');
      if (!text) return;
      trackCartError({ error_message: text, element: node });
    });

    scanRoot.querySelectorAll('.cart-discount__error:not(.hidden)').forEach((container) => {
      if (!isElementVisible(container)) return;
      const discountForm = container.closest('cart-discount-component')?.querySelector('input[name="discount"]');
      const couponCode =
        discountForm instanceof HTMLInputElement ? discountForm.value : '';

      container.querySelectorAll('.cart-discount__error-text:not(.hidden)').forEach((n) => {
        if (!isElementVisible(n)) return;
        const text = normalizeCartErrorMessage(n.textContent || '');
        if (!text) return;
        const isShipping = n.dataset.cartErrorType === 'shipping';
        trackCartError({
          error_category: 'coupon',
          error_message: text,
          coupon_code: couponCode,
          coupon_reason: isShipping ? 'not_applicable' : 'invalid',
          element: container,
        });
      });
    });

    const productRoot = getProductErrorScanRoot();
    if (!productRoot) return;

    productRoot.querySelectorAll('.cart-page__item-error').forEach((errEl) => {
      if (!isElementVisible(errEl)) return;
      const title = (errEl.querySelector('.cart-page__item-error-title')?.textContent || '').trim();
      const description = (errEl.querySelector('.cart-page__item-error-text')?.textContent || '').trim();
      const text = normalizeCartErrorMessage([title, description].filter(Boolean).join(' '));
      if (!text) return;
      trackCartError({
        error_message: text,
        error_category: 'product_availability',
        element: errEl,
      });
    });

    productRoot.querySelectorAll('td.cart-items__error:not(.hidden)').forEach((cell) => {
      if (!isElementVisible(cell)) return;
      const text = normalizeCartErrorMessage(cell.querySelector('.cart-item__error-text')?.textContent || '');
      if (!text) return;
      const qtyInput = cell
        .closest('tr')
        ?.querySelector(`cart-quantity-selector-component input[data-cart-line]`);
      const requestedQty =
        qtyInput instanceof HTMLInputElement ? parseInt(qtyInput.value, 10) : undefined;
      trackCartError({
        error_message: text,
        element: cell,
        requested_quantity: Number.isFinite(requestedQty) ? requestedQty : undefined,
      });
    });

    productRoot.querySelectorAll('.producterror').forEach((errEl) => {
      if (!isElementVisible(errEl)) return;
      const text = normalizeCartErrorMessage(errEl.textContent || '');
      if (!text) return;
      trackCartError({ error_message: text, element: errEl });
    });
  }

  /**
   * Theme cart:error (add to cart failures) and cart:update errors.
   */
  function setupCartErrorEventListeners() {
    if (!isCartErrorTrackingEnabled()) return;

    document.addEventListener(
      'cart:error',
      (event) => {
        const data = event.detail?.data || {};
        const message =
          normalizeCartErrorMessage(data.message) ||
          normalizeCartErrorMessage(
            typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors || '')
          );
        if (!message) return;

        trackCartError({
          error_message: message,
          error_category: classifyCartErrorCategory(message),
          subsystem: 'cart_api',
        });
      },
      { passive: true }
    );

    document.addEventListener(
      'cart:update',
      (event) => {
        const detail = event.detail;
        if (!detail?.data?.didError) return;
        // Add-to-cart failures also emit cart:error with the real message.
        const source = detail.data?.source;
        if (source === 'product-form-component' || source === 'quick-add') return;
        const message = normalizeCartErrorMessage(detail.data.message || '');
        if (!message) return;
        trackCartError({
          error_message: message,
          subsystem: 'cart_api',
        });
      },
      { passive: true }
    );
  }

  /**
   * Schedules cart error capture after cart section updates (quantity, drawer refresh, etc.).
   */
  function setupCartErrorCapture() {
    if (!isCartErrorTrackingEnabled()) return;

    setupCartErrorEventListeners();

    const schedule = () => requestAnimationFrame(() => captureCartErrorsForGA());

    document.addEventListener('cart-drawer:open', schedule, { passive: true });
    document.addEventListener('cart:update', () => schedule(), { passive: true });
    document.addEventListener('discount:update', () => schedule(), { passive: true });
  }

  /**
   * View Item Event (Product Page)
   * Fires when a user views a product detail page
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item
   */
  function fireViewItemEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItem) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;
    // Advance PDP owns view_item from the active variant (see advance-pdp.js).
    if (document.getElementById('products-data')) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.product) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const product = window.gtmConfig.product;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const eventData = {
      event: 'view_item',
      // Product-level tracking data
      masterProductId: String(product.masterProductId),
      item_image_url: product.item_image_url || '',
      ecommerce: {
        currency: currency,
        value: parseFloat(product.price) || 0,
        items: [
          {
            item_id: String(product.item_id),
            ...masterProductIdField(product),
            ams_item_id: resolveAmsItemId(product),
            item_name: product.item_name,
            item_brand: product.item_brand || '',
            item_category: product.item_category || '',
            item_variant: product.item_variant || '',
            item_image_url: product.item_image_url || '',
            price: parseFloat(product.price) || 0,
            quantity: 1,
          },
        ],
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('view_item', eventData);
  }

  /**
   * Viewed Category Event
   * Fires on every collection page load. GTM supplies its own internal gtm fields.
   */
  function fireViewedCategoryEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewedCategory) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'collection') return;
    // @ts-ignore - GTM config is injected by Liquid template
    const collection = window.gtmConfig.collection;
    if (!collection) return;

    const eventData = {
      ads_data_redaction: true,
      event: 'viewed_category',
      path: window.location.pathname,
      url: window.location.href,
      category_name: collection.title,
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('viewed_category', eventData);
  }

  /**
   * View Item List Event (Collection Page)
   * Fires when a user views a list of products
   * Includes both GA4 view_item_list event and legacy category tracking data
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list
   */
  function fireViewItemListEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItemList) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'collection') return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.collection) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const collection = window.gtmConfig.collection;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    // Calculate total value of visible items
    const totalValue = collection.items.reduce((sum, item) => {
      return sum + (parseFloat(item.price) || 0);
    }, 0);

    const eventData = {
      event: 'view_item_list',
      // Legacy category tracking data
      ecomm_pagetype: 'category',
      ecomm_category: collection.title,
      categoryID: String(collection.id),
      categoryName: collection.title,
      categoryimageURL: collection.imageUrl || '',
      // GA4 E-commerce data
      ecommerce: {
        currency: currency,
        item_list_id: String(collection.id),
        item_list_name: collection.title,
        items: collection.items.map((item, index) => ({
          item_id: String(item.item_id),
          ...masterProductIdField(item),
          ams_item_id: resolveAmsItemId(item),
          item_name: item.item_name,
          item_brand: item.item_brand || '',
          item_category: item.item_category || collection.title,
          item_variant: item.item_variant || '',
          item_list_id: String(collection.id),
          item_list_name: collection.title,
          index: item.index || index + 1,
          price: parseFloat(item.price) || 0,
        })),
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('view_item_list', eventData);
  }

  /** @type {string} */
  let lastViewCartSignature = '';
  let lastViewCartTime = 0;

  /**
   * @returns {Map<string, ReturnType<typeof getLineMetaFromElement>>}
   */
  function getRenderedCartLineMetadata() {
    // @ts-ignore - GTM config is injected by Liquid template
    const selector = window.gtmConfig.pageType === 'cart'
      ? '.cart-page__products-list .cart-gtm4-row[data-key]'
      : 'cart-drawer-component .cart-gtm4-row[data-key]';
    const metadata = new Map();

    for (const row of document.querySelectorAll(selector)) {
      if (!(row instanceof HTMLElement)) continue;
      const key = row.dataset.key;
      if (!key || metadata.has(key)) continue;
      metadata.set(key, getLineMetaFromElement(row));
    }

    return metadata;
  }

  /**
   * @param {Object} item
   * @param {Object|null} cartApi
   * @returns {string}
   */
  function getCartItemCoupons(item, cartApi) {
    const codes = [];
    const lineAllocations = Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : [];
    const cartDiscounts = Array.isArray(cartApi?.cart_level_discount_applications)
      ? cartApi.cart_level_discount_applications
      : [];

    const discounts = [
      ...lineAllocations
        .map((allocation) => allocation?.discount_application)
        .filter((discount) => discount?.type === 'discount_code'),
      ...cartDiscounts.filter((discount) => discount?.type === 'discount_code'),
    ];

    for (const discount of discounts) {
      const code = discount?.title || discount?.code;
      if (!code || codes.includes(String(code))) continue;
      codes.push(String(code));
    }

    return codes.join(', ');
  }

  /**
   * @returns {Promise<Object|null>}
   */
  async function fetchCurrentCartForView() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * View Cart Event
   * Fires on the cart page and whenever the cart drawer opens.
   * @param {'page'|'drawer'} [source]
   * @returns {Promise<void>}
   */
  async function fireViewCartEvent(source = 'page') {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewCart) return;
    // @ts-ignore - GTM config is injected by Liquid template
    const isCartPage = window.gtmConfig.pageType === 'cart';
    if (!isCartPage && source !== 'drawer') return;

    // @ts-ignore - GTM config is injected by Liquid template
    const configuredCart = window.gtmConfig.cart || { value: 0, items: [] };
    // @ts-ignore - GTM config is injected by Liquid template
    const configuredItems = Array.isArray(configuredCart.items) ? configuredCart.items : [];
    const configuredByVariant = new Map(
      configuredItems.map((item) => [String(item.variant_id || item.item_id), item])
    );
    const renderedByKey = getRenderedCartLineMetadata();
    const cartApi = await fetchCurrentCartForView();
    const apiItems = Array.isArray(cartApi?.items) ? cartApi.items : configuredItems;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = cartApi?.currency || window.gtmConfig.currency || 'GBP';

    const items = apiItems.map((item) => {
      const variantId = String(item.id || item.variant_id || item.item_id || '');
      const configured = configuredByVariant.get(variantId) || {};
      const rendered = renderedByKey.get(String(item.key || '')) || {};
      const quantity = Number(item.quantity ?? configured.quantity) || 1;
      const originalLinePrice = Number(item.original_line_price);
      const finalLinePrice = Number(item.final_line_price);
      const configuredDiscount = Number(configured.discount) || 0;
      const discount =
        Number.isFinite(originalLinePrice) && Number.isFinite(finalLinePrice)
          ? Number(((originalLinePrice - finalLinePrice) / quantity / 100).toFixed(2))
          : configuredDiscount;
      const originalPrice = Number(item.original_price ?? item.price);
      const price = Number.isFinite(originalPrice)
        ? Number((originalPrice / 100).toFixed(2))
        : Number(configured.price) || 0;

      return {
        item_name: item.product_title || item.title || configured.item_name || rendered.product_title || '',
        item_id: String(item.sku || rendered.variant_sku || configured.variant_sku || variantId),
        ams_item_id: resolveAmsItemId({ ...configured, ...rendered, variant_id: variantId }),
        price,
        item_brand: item.vendor || configured.item_brand || rendered.item_brand || '',
        category: item.product_type || configured.category || rendered.category || '',
        item_variant: item.variant_title || configured.item_variant || rendered.item_variant || '',
        quantity,
        currency,
        coupons: getCartItemCoupons(item, cartApi),
        discount,
        warranty_selected: Boolean(configured.warranty_selected || rendered.warranty_selected),
        articleType: item.product_type || configured.articleType || rendered.articleType || '',
        color: rendered.color || configured.color || '',
        size: rendered.size || configured.size || '',
        mainStoneName: rendered.mainStoneName || configured.mainStoneName || '',
        google_business_vertical: 'retail',
        metalType: rendered.metalType || configured.metalType || '',
      };
    });

    const value = Number(
      cartApi && typeof cartApi.total_price === 'number'
        ? (cartApi.total_price / 100).toFixed(2)
        : parseFloat(configuredCart.value) || 0
    );
    const signature = `${source}:${value}:${items
      .map((item) => `${item.item_id}:${item.quantity}`)
      .join('|')}`;
    const now = Date.now();
    if (signature === lastViewCartSignature && now - lastViewCartTime < 1000) return;
    lastViewCartSignature = signature;
    lastViewCartTime = now;

    clearEcommerceData();

    const eventData = {
      event: 'view_cart',
      ecommerce: {
        currency,
        value,
        items,
      },
    };

    const viewCartPayload = await pushGtmEvent(eventData);
    gtmDebugLog('view_cart', viewCartPayload);
  }

  /**
   * Tracks cart drawer views after its section content has rendered.
   */
  function setupViewCartDrawerTracking() {
    document.addEventListener(
      'cart-drawer:open',
      () => {
        // @ts-ignore - GTM config is injected by Liquid template
        if (window.gtmConfig.pageType === 'cart') return;
        void fireViewCartEvent('drawer');
      },
      { passive: true }
    );
  }

  let searchTrackingTimer = 0;
  /** First schedule timestamp for max-wait debounce. */
  let searchScheduleStartedAt = 0;
  /** @type {{term:string, resultsCount:number, itemCount:number}|null} */
  let lastTrackedSearch = null;
  /** @type {AbortController|null} */
  let searchAnalyticsController = null;
  /** Retry budget while waiting for Prefixbox/native cards to appear. */
  let searchRenderWaitAttempts = 0;
  const SEARCH_RENDER_WAIT_MAX = 8;
  let searchHistoryTrackingInstalled = false;
  let lastSeenSearchTerm = '';
  let searchTrackingInstalled = false;

  /**
   * @param {string} href
   * @returns {string}
   */
  function getProductHandleFromUrl(href) {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  /**
   * Visible SERP root. Prefixbox may inject outside #MainContent, so fall back to body.
   * @returns {ParentNode}
   */
  function getSearchResultsRoot() {
    return (
      document.getElementById('MainContent') ||
      document.querySelector('[data-prefixbox-search], .prefixbox-search, #prefixbox-search') ||
      document.body
    );
  }

  /**
   * Returns product handles in the same order as the rendered SERP.
   * Duplicate card links are collapsed.
   * @returns {string[]}
   */
  function getRenderedSearchProductHandles() {
    const handles = [];
    const root = getSearchResultsRoot();
    if (!root) return handles;

    for (const anchor of root.querySelectorAll('a[href*="/products/"]')) {
      if (!(anchor instanceof HTMLAnchorElement)) continue;
      // Skip header/nav/wishlist chrome when scanning body-wide.
      if (anchor.closest('header, #header-group, .header, nav, .predictive-search')) continue;
      const handle = getProductHandleFromUrl(anchor.href);
      if (!handle || handles.includes(handle)) continue;
      handles.push(handle);
    }

    return handles;
  }

  /**
   * Fetches a single Liquid-rendered metadata payload for the active query.
   * This enriches Prefixbox cards with custom.vsfccmaster without one request per product.
   * @param {string} searchTerm
   * @returns {Promise<{search_term:string, search_results_count:number, items:Array<Object>}|null>}
   */
  async function fetchSearchAnalyticsPayload(searchTerm) {
    searchAnalyticsController?.abort();
    searchAnalyticsController = new AbortController();
    const url = new URL('/search', window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'section-rendering-search-analytics');

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        signal: searchAnalyticsController.signal,
      });
      if (!response.ok) {
        gtmDebugLog('view_search_result', {
          status: 'analytics_section_http_error',
          httpStatus: response.status,
          searchTerm,
        });
        return null;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const payload = doc.querySelector('[data-gtm-search-analytics]');
      if (!payload?.textContent) {
        gtmDebugLog('view_search_result', {
          status: 'analytics_section_missing_payload',
          searchTerm,
        });
        return null;
      }
      return JSON.parse(payload.textContent);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return null;
      gtmDebugLog('view_search_result', {
        status: 'analytics_section_fetch_failed',
        searchTerm,
        error: String(error),
      });
      return null;
    }
  }

  /**
   * View Search Result Event
   * Fires after native or Prefixbox results settle, including zero-result searches.
   * Does not require the analytics section to succeed — that only enriches AMS ids.
   * @param {boolean} [force]
   * @returns {Promise<void>}
   */
  /**
   * True only on the full search results page (/search?q=...), not predictive autocomplete.
   * @returns {boolean}
   */
  function isSearchResultsPage() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig?.viewSearchResult) return false;
    const path = window.location.pathname || '';
    // @ts-ignore - GTM config is injected by Liquid template
    const isSearchTemplate = window.gtmConfig.pageType === 'search' || path.includes('/search');
    return isSearchTemplate && new URL(window.location.href).searchParams.has('q');
  }

  async function fireViewSearchResultEvent(force = false) {
    if (!isSearchResultsPage()) {
      gtmDebugLog('view_search_result', {
        status: 'skipped_not_search_results_page',
        path: window.location.pathname,
        // @ts-ignore
        pageType: window.gtmConfig?.pageType,
        note: 'Predictive/autocomplete overlay does not fire this event — open /search?q=...',
      });
      return;
    }
    const currentUrl = new URL(window.location.href);
    const searchTerm = (currentUrl.searchParams.get('q') || '').trim();
    if (!searchTerm) return;

    if (!lastTrackedSearch || lastTrackedSearch.term !== searchTerm) {
      searchRenderWaitAttempts = 0;
    }

    if (!force && lastTrackedSearch && lastTrackedSearch.term === searchTerm) {
      // Prefixbox mutates the SERP repeatedly; only skip when this query was already
      // tracked with the same visible result cardinality.
      const provisionalHandles = getRenderedSearchProductHandles();
      if (
        provisionalHandles.length === lastTrackedSearch.itemCount ||
        (lastTrackedSearch.resultsCount === 0 && provisionalHandles.length === 0)
      ) {
        return;
      }
    }

    const payload = await fetchSearchAnalyticsPayload(searchTerm);

    // A newer query was submitted while the metadata request was in flight.
    const latestTerm = (new URL(window.location.href).searchParams.get('q') || '').trim();
    if (latestTerm !== searchTerm) return;

    const metadataItems = Array.isArray(payload?.items) ? payload.items : [];
    const metadataByHandle = new Map(metadataItems.map((item) => [String(item.handle || ''), item]));
    const renderedHandles = getRenderedSearchProductHandles();
    const shopifyResultsCount = Number(payload?.search_results_count) || 0;

    // Wait briefly for Prefixbox/native cards when Shopify (or the UI) still expects hits.
    const expectVisibleCards = shopifyResultsCount > 0 || document.querySelector('[class*="prefixbox"], [id*="prefixbox"]');
    if (!force && expectVisibleCards && renderedHandles.length === 0 && searchRenderWaitAttempts < SEARCH_RENDER_WAIT_MAX) {
      searchRenderWaitAttempts += 1;
      scheduleSearchResultTracking(700);
      return;
    }
    searchRenderWaitAttempts = 0;

    const orderedItems = renderedHandles.length
      ? renderedHandles.map((handle) => {
          const matched = metadataByHandle.get(handle);
          if (matched) return matched;
          return {
            handle,
            item_id: '',
            ams_item_id: '',
            item_name: handle,
          };
        })
      : metadataItems;
    // Prefer visible SERP count (Prefixbox) when DOM cards exist; otherwise Shopify count.
    const effectiveResultsCount = renderedHandles.length > 0
      ? renderedHandles.length
      : shopifyResultsCount;

    const items = orderedItems.map((item, index) => ({
      item_id: String(item.item_id || ''),
      ams_item_id: resolveAmsItemId(item),
      item_name: item.item_name || '',
      index,
      item_list_name: 'Search Results',
      item_list_id: 'search_results',
    }));

    if (
      !force &&
      lastTrackedSearch &&
      lastTrackedSearch.term === searchTerm &&
      lastTrackedSearch.resultsCount === effectiveResultsCount &&
      lastTrackedSearch.itemCount === items.length
    ) {
      return;
    }

    clearEcommerceData();

    const eventData = {
      event: 'view_search_result',
      search_term: searchTerm,
      search_results_count: effectiveResultsCount,
      path: window.location.pathname,
      url: window.location.href,
      ecommerce: {
        items,
      },
    };

    const searchPayload = await pushGtmEvent(eventData);
    lastTrackedSearch = {
      term: searchTerm,
      resultsCount: effectiveResultsCount,
      itemCount: items.length,
    };
    gtmDebugLog('view_search_result', searchPayload);
    console.info('[GTM] view_search_result pushed', {
      search_term: searchTerm,
      search_results_count: effectiveResultsCount,
      items: items.length,
    });
  }

  /**
   * Debounces DOM-driven search callbacks until the results UI has settled.
   * Prefixbox keeps mutating the SERP (images, facets, layout), which would
   * otherwise reset a plain debounce forever and prevent auto-fire.
   * @param {number} [delay]
   * @param {number} [maxWait]
   */
  function scheduleSearchResultTracking(delay = 500, maxWait = 2000) {
    const now = Date.now();
    if (!searchScheduleStartedAt) {
      searchScheduleStartedAt = now;
    }

    window.clearTimeout(searchTrackingTimer);

    const elapsed = now - searchScheduleStartedAt;
    const remainingMax = Math.max(0, maxWait - elapsed);
    const wait = Math.min(delay, remainingMax);

    searchTrackingTimer = window.setTimeout(() => {
      searchScheduleStartedAt = 0;
      void fireViewSearchResultEvent();
    }, wait);
  }

  /**
   * Detect Prefixbox/AJAX search term changes that use history.pushState
   * (popstate alone does not fire for pushState).
   */
  function setupSearchHistoryTracking() {
    if (searchHistoryTrackingInstalled) return;
    searchHistoryTrackingInstalled = true;

    const notifySearchUrlChange = () => {
      if (!window.location.pathname.includes('/search')) return;
      const nextTerm = (new URL(window.location.href).searchParams.get('q') || '').trim();
      if (nextTerm === lastSeenSearchTerm) return;
      lastSeenSearchTerm = nextTerm;
      searchRenderWaitAttempts = 0;
      lastTrackedSearch = null;
      scheduleSearchResultTracking(300, 1500);
    };

    const originalPushState = history.pushState.bind(history);
    history.pushState = function (...args) {
      originalPushState(...args);
      notifySearchUrlChange();
    };

    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = function (...args) {
      originalReplaceState(...args);
      notifySearchUrlChange();
    };

    window.addEventListener('popstate', notifySearchUrlChange);
  }

  /**
   * Prefixbox is app-owned, so observe its rendered output and also support explicit callbacks.
   */
  function setupSearchResultTracking() {
    if (!isSearchResultsPage()) {
      gtmDebugLog('view_search_result', {
        status: 'setup_skipped',
        path: window.location.pathname,
        // @ts-ignore
        pageType: window.gtmConfig?.pageType,
      });
      return;
    }

    lastSeenSearchTerm = (new URL(window.location.href).searchParams.get('q') || '').trim();
    setupSearchHistoryTracking();

    if (!searchTrackingInstalled) {
      searchTrackingInstalled = true;
      const root = getSearchResultsRoot();
      if (root) {
        const observer = new MutationObserver(() => scheduleSearchResultTracking(500, 2000));
        observer.observe(root, { childList: true, subtree: true });
      }

      document.addEventListener('prefixbox:search-results-rendered', () => {
        scheduleSearchResultTracking(200, 1000);
      });
      document.addEventListener('prefixbox:search-complete', () => {
        scheduleSearchResultTracking(200, 1000);
      });
    }

    gtmDebugLog('view_search_result', {
      status: 'setup_armed',
      searchTerm: lastSeenSearchTerm,
    });
    console.info('[GTM] view_search_result armed for', lastSeenSearchTerm);

    // Immediate attempt (force) so we don't depend only on Prefixbox mutations.
    void fireViewSearchResultEvent(true);
    // Follow-up after cards typically finish rendering.
    scheduleSearchResultTracking(800, 800);
    window.setTimeout(() => {
      void fireViewSearchResultEvent();
    }, 2500);
  }

  /**
   * Keep search tracking alive across history-driven SERP navigations.
   */
  function setupGlobalSearchPageWatcher() {
    setupSearchHistoryTracking();

    const maybeArmSearchTracking = () => {
      if (!isSearchResultsPage()) return;
      const nextTerm = (new URL(window.location.href).searchParams.get('q') || '').trim();
      if (nextTerm && nextTerm === lastTrackedSearch?.term) return;
      setupSearchResultTracking();
    };

    window.addEventListener('popstate', maybeArmSearchTracking);
    document.addEventListener('shopify:section:load', maybeArmSearchTracking);
  }

  /**
   * Add to Cart Event
   * Fires when a user adds an item to their cart
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart
   * @param {Object} productData - Product data object
   * @param {string|number} productData.masterProductId - Shopify product ID
   * @param {string|number} productData.item_id - Variant SKU
   * @param {string} productData.item_name - Product name
   * @param {string} [productData.item_brand] - Product brand/vendor
   * @param {string} [productData.item_category] - Product category
   * @param {string} [productData.item_variant] - Variant title
   * @param {string} [productData.item_image_url] - Product image URL
   * @param {number} productData.price - Product price
   * @param {number} [productData.quantity] - Quantity added
   * @param {string} [productData.currency] - Currency code
   */
  function trackAddToCart(productData) {
    console.log('[GTM Cart Debug] trackAddToCart()', {
      productData,
      stack: new Error().stack,
    });

    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.addToCart) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';
    const quantity = productData.quantity || 1;
    const price = parseFloat(productData.price) || 0;
    const value = price * quantity;

    clearEcommerceData();

    const eventData = {
      event: 'add_to_cart',
      // Product-level tracking data
      masterProductId: String(productData.masterProductId || productData.item_id),
      item_image_url: productData.item_image_url || '',
      ecommerce: {
        currency: currency,
        value: value,
        items: [
          {
            item_id: String(productData.item_id),
            ...masterProductIdField(productData),
            ams_item_id: resolveAmsItemId(productData),
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            item_image_url: productData.item_image_url || '',
            price: price,
            quantity: quantity,
          },
        ],
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('add_to_cart', eventData);
  }

  /**
   * Remove from Cart Event
   * Fires when a user removes an item from their cart
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#remove_from_cart
   * @param {Object} productData - Product data object
   */
  function trackRemoveFromCart(productData) {
    console.log('[GTM Cart Debug] trackRemoveFromCart()', {
      productData,
      stack: new Error().stack,
    });

    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.addToCart) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';
    const quantity = productData.quantity || 1;
    const price = parseFloat(productData.price) || 0;
    const value = price * quantity;

    clearEcommerceData();

    const eventData = {
      event: 'remove_from_cart',
      ecommerce: {
        currency: currency,
        value: value,
        items: [
          {
            item_id: String(productData.item_id),
            ams_item_id: resolveAmsItemId(productData),
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            price: price,
            quantity: quantity,
          },
        ],
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('remove_from_cart', eventData);
  }

  /**
   * Purchase Event
   * Fires when a user completes a purchase
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
   * @param {Object} orderData - Order data object
   * @param {string} orderData.transaction_id - Shopify Order Name (e.g. TJC12345), not numeric order id (TWS-1081)
   * @param {number} orderData.value - Order total value
   * @param {string} [orderData.currency] - Currency code
   * @param {number} [orderData.tax] - Tax amount
   * @param {number} [orderData.shipping] - Shipping cost
   * @param {number} [orderData.discount] - Transaction-level discount total
   * @param {string} [orderData.coupon] - Coupon code used
   * @param {Array} orderData.items - Array of order items
   */
  function trackPurchase(orderData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.purchaseEvent) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = orderData.currency || window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const transactionId = String(orderData.transaction_id || '');
    const purchaseValue = parseFloat(orderData.value) || 0;
    const discountTotal = parseFloat(orderData.discount) || 0;
    // TWS-976: net revenue after discount/coupon; value itself stays unchanged.
    const netRevenue = Number(Math.max(0, purchaseValue - discountTotal).toFixed(2));

    const eventData = {
      event: 'purchase',
      transaction_id: transactionId,
      orderId: transactionId,
      'Transactional ID': transactionId,
      ecommerce: {
        transaction_id: transactionId,
        // Gross / GTM subtotal (caller must pass merchandise value; unchanged by TWS-976)
        value: purchaseValue,
        net_revenue: netRevenue,
        currency: currency,
        tax: parseFloat(orderData.tax) || 0,
        shipping: parseFloat(orderData.shipping) || 0,
        discount: discountTotal,
        coupon: orderData.coupon || '',
        items: (orderData.items || []).map((item, index) => ({
          item_id: String(item.item_id || item.id),
          ...masterProductIdField(item),
          ams_item_id: resolveAmsItemId(item),
          item_name: item.item_name || item.name || '',
          item_brand: item.item_brand || item.brand || 'TJC',
          item_category: item.item_category || item.category || '',
          item_variant: item.item_variant || item.variant || '',
          price: parseFloat(item.price) || 0,
          quantity: item.quantity || 1,
          discount: parseFloat(item.discount) || 0,
          coupon: item.coupon || '',
          index: index + 1,
        })),
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('purchase', eventData);
  }

  /**
   * Banner Tracking Event
   * Tracks banner impressions and clicks
   * @param {string} eventType - Event type (impression, click)
   * @param {Object} bannerData - Banner data object
   */
  function trackBannerEvent(eventType, bannerData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.bannerTracking) return;

    const eventData = {
      event: `banner_${eventType}`,
      banner_id: bannerData.id || '',
      banner_name: bannerData.name || '',
      banner_position: bannerData.position || '',
      banner_url: bannerData.url || '',
    };

    void pushGtmEvent(eventData);
    gtmDebugLog(`banner_${eventType}`, eventData);
  }

  /**
   * BID Event Tracking
   * Tracks BID (Browser ID) events
   * @param {Object} bidData - BID data object
   */
  function trackBidEvent(bidData) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.bidEvent) return;

    const eventData = {
      event: 'bid_event',
      bid_id: bidData.id || '',
      bid_timestamp: bidData.timestamp || new Date().getTime(),
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('bid_event', eventData);
  }

  /**
   * Select Item Event (Product Click from List)
   * Fires when a user clicks on a product in a list
   * @param {Object} productData - Product data
   * @param {string} [listId] - List/Collection ID
   * @param {string} [listName] - List/Collection name
   */
  function trackSelectItem(productData, listId, listName) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItemList) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const currency = productData.currency || window.gtmConfig.currency || 'USD';

    clearEcommerceData();

    const eventData = {
      event: 'select_item',
      ecommerce: {
        currency: currency,
        item_list_id: listId || '',
        item_list_name: listName || '',
        items: [
          {
            item_id: String(productData.item_id),
            ams_item_id: resolveAmsItemId(productData),
            item_name: productData.item_name || '',
            item_brand: productData.item_brand || '',
            item_category: productData.item_category || '',
            item_variant: productData.item_variant || '',
            item_list_id: listId || '',
            item_list_name: listName || '',
            index: productData.index || 1,
            price: parseFloat(productData.price) || 0,
          },
        ],
      },
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('select_item', eventData);
  }

  /**
   * Handle variant change on PDP
   * Fires view_item event when user switches to a different variant
   * @param {Object} variant - Shopify variant object from the event
   * @param {string|number} variant.id - Variant ID
   * @param {string} [variant.sku] - Variant SKU
   * @param {string} [variant.title] - Variant title
   * @param {number} [variant.price] - Variant price in cents
   * @param {Object} [variant.featured_media] - Variant featured media
   * @param {Object} [variant.featured_media.preview_image] - Preview image object
   * @param {string} [variant.featured_media.preview_image.src] - Image source URL
   */
  function handleVariantChange(variant) {
    // @ts-ignore - GTM config is injected by Liquid template
    if (!window.gtmConfig.viewItem) return;
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;
    if (!variant) return;

    // @ts-ignore - GTM config is injected by Liquid template
    const productConfig = window.gtmConfig.product;
    // @ts-ignore - GTM config is injected by Liquid template
    const currency = window.gtmConfig.currency || 'USD';

    if (!productConfig) return;

    // Calculate price (Shopify returns price in cents)
    const price = variant.price ? variant.price / 100 : parseFloat(productConfig.price) || 0;

    // Get variant image URL from featured_media (this theme's structure)
    const variantImageUrl =
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      productConfig.item_image_url ||
      '';

    // Get variant title
    const variantTitle = variant.title || variant.name || variant.option1 || '';
    const variantAmsId = productConfig.variantAmsIds?.[String(variant.id)];
    const amsItemId =
      variantAmsId != null && String(variantAmsId).trim() !== ''
        ? String(variantAmsId)
        : resolveAmsItemId({
            vsfccmaster: productConfig.vsfccmaster,
            variant_id: variant.id,
            masterProductId: productConfig.masterProductId,
            item_id: variant.sku || String(variant.id),
          });

    clearEcommerceData();

    const eventData = {
      event: 'view_item',
      // Product-level tracking data
      masterProductId: String(productConfig.masterProductId),
      item_image_url: variantImageUrl,
      ecommerce: {
        currency: currency,
        value: price,
        items: [
          {
            item_id: variant.sku || String(variant.id),
            ...masterProductIdField(productConfig),
            ams_item_id: amsItemId,
            item_name: productConfig.item_name,
            item_brand: productConfig.item_brand || '',
            item_category: productConfig.item_category || '',
            item_variant: variantTitle,
            item_image_url: variantImageUrl,
            price: price,
            quantity: 1,
          },
        ],
      },
    };

    // Update the gtmConfig.product with new variant data for add_to_cart tracking
    // @ts-ignore - GTM config is injected by Liquid template
    window.gtmConfig.product = {
      ...productConfig,
      item_id: variant.sku || String(variant.id),
      ams_item_id: amsItemId,
      vsfccmaster: variantAmsId ?? productConfig.vsfccmaster,
      variant_id: variant.id,
      item_variant: variantTitle,
      item_image_url: variantImageUrl,
      price: price,
      sku: variant.sku || '',
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('view_item (variant change)', eventData);
  }

  /**
   * Listen for variant change events on product pages
   * This theme uses ThemeEvents.variantUpdate ('variant:update') from events.js
   * Only listen to variant:update to avoid duplicate events
   */
  function setupVariantChangeListener() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== 'product') return;

    // Track the last variant ID to prevent duplicate events
    let lastTrackedVariantId = null;

    // Listen for variant:update event (this theme's primary event)
    // Event contains: resource (variant object), sourceId, data (html, productId)
    document.addEventListener('variant:update', function (event) {
      // @ts-ignore - event detail from theme
      const detail = event.detail;
      const variant = detail?.resource;

      // Only track if variant exists and is different from last tracked
      if (variant && variant.id !== lastTrackedVariantId) {
        lastTrackedVariantId = variant.id;
        handleVariantChange(variant);
      }
    });

    gtmDebugLog('Variant change listener', { status: 'initialized' });
  }

  /**
   * Listen for cart:update events from the theme
   * This captures add_to_cart / remove_from_cart from product-form, quick-add, and cart line changes
   */
  function setupCartEventListener() {
    document.addEventListener('cart:update', function (event) {
      // @ts-ignore - event detail from theme
      const detail = event.detail;
      const data = detail?.data;

      console.log('[GTM Cart Debug] cart:update received', {
        source: data?.source,
        action: data?.action,
        didError: data?.didError,
        delta: data?.delta,
        line: data?.line,
        productData: data?.productData,
      });

      if (data?.didError) {
        console.log('[GTM Cart Debug] skipped — cart operation reported didError');
        return;
      }

      const source = data?.source;
      const action = data?.action;

      // PDP / PLP add flows
      if (source === 'product-form-component' || source === 'quick-add' || source === 'iw-stepper-lp') {
        if (action && action !== 'add') {
          console.log('[GTM Cart Debug] skipped — not an add action for', source, action);
          return;
        }

        if (data.productData) {
          trackAddToCart(data.productData);
          return;
        }

        // advance-pdp fires a second cart:update (drawer refresh) without productData — skip GTM
        if (source === 'iw-stepper-lp') {
          console.log('[GTM Cart Debug] skipped — iw-stepper-lp refresh without productData');
          return;
        }

        // @ts-ignore - GTM config is injected by Liquid template
        const productConfig = window.gtmConfig.product;
        if (productConfig) {
          trackAddToCart({
            masterProductId: productConfig.masterProductId,
            item_id: productConfig.item_id,
            ams_item_id: productConfig.ams_item_id,
            vsfccmaster: productConfig.vsfccmaster,
            variant_id: productConfig.variant_id,
            item_name: productConfig.item_name,
            item_brand: productConfig.item_brand,
            item_category: productConfig.item_category,
            item_variant: productConfig.item_variant,
            item_image_url: productConfig.item_image_url,
            price: productConfig.price,
            quantity: data.delta || productConfig.quantity || 1,
          });
          return;
        }

        if (data.productId) {
          trackAddToCart({
            masterProductId: data.productId,
            item_id: data.productId,
            item_name: '',
            price: 0,
            quantity: data.delta || 1,
          });
        }
        return;
      }

      // Mini-cart / cart page quantity changes and removals
      if (source === 'cart-items-component') {
        const productData = data.productData;
        if (!productData) {
          console.log('[GTM Cart Debug] skipped — cart-items-component missing productData');
          return;
        }

        if (action === 'remove') {
          trackRemoveFromCart(productData);
          return;
        }

        if (action === 'update') {
          const delta = Number(data.delta);
          if (!Number.isFinite(delta) || delta === 0) {
            console.log('[GTM Cart Debug] skipped — cart update with zero/invalid delta', delta);
            return;
          }

          if (delta > 0) {
            trackAddToCart({ ...productData, quantity: delta });
          } else {
            trackRemoveFromCart({ ...productData, quantity: Math.abs(delta) });
          }
          return;
        }

        console.log('[GTM Cart Debug] skipped — unhandled cart-items-component action', action);
      }
    });

    gtmDebugLog('Cart event listener', { status: 'initialized' });
  }

  /**
   * TWS-1048: once per full page load (avoids re-init / section:load doubles).
   * @type {boolean}
   */
  let pageTimingFired = false;

  /**
   * Navigation Timing Level 2 entry, with legacy performance.timing fallback.
   * @returns {PerformanceNavigationTiming | { startTime: number, requestStart: number, responseStart: number, loadEventEnd: number } | null}
   */
  function getNavigationTimingEntry() {
    try {
      if (typeof performance === 'undefined') return null;

      const entries =
        typeof performance.getEntriesByType === 'function'
          ? performance.getEntriesByType('navigation')
          : null;
      if (entries?.length) return /** @type {PerformanceNavigationTiming} */ (entries[0]);

      // Legacy Timing API (Safari older / rare)
      // @ts-ignore - deprecated but still present in some browsers
      const t = performance.timing;
      if (!t?.navigationStart) return null;

      return {
        startTime: 0,
        requestStart: Math.max(0, t.requestStart - t.navigationStart),
        responseStart: Math.max(0, t.responseStart - t.navigationStart),
        loadEventEnd: Math.max(0, t.loadEventEnd - t.navigationStart),
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * @returns {{ pageLoadTime: number, serverResponseTime: number } | null}
   */
  function readPageTimingMetrics() {
    const nav = getNavigationTimingEntry();
    if (!nav) return null;

    const loadEventEnd = Number(nav.loadEventEnd) || 0;
    const startTime = Number(nav.startTime) || 0;
    const responseStart = Number(nav.responseStart) || 0;
    const requestStart = Number(nav.requestStart) || 0;

    // loadEventEnd is 0 until the load event has finished processing
    if (loadEventEnd <= 0) return null;

    const pageLoadTime = Math.round(loadEventEnd - startTime);
    const serverResponseTime = Math.round(responseStart - requestStart);

    if (pageLoadTime <= 0 || serverResponseTime <= 0) return null;

    return { pageLoadTime, serverResponseTime };
  }

  /**
   * Push SFCC-named timing events once metrics are available (TWS-1048).
   * page_load_time = loadEventEnd - startTime
   * server_response_time = responseStart - requestStart (TTFB)
   */
  function trackPageTiming() {
    if (pageTimingFired) return;

    const metrics = readPageTimingMetrics();
    if (!metrics) return;

    pageTimingFired = true;

    const pagePath = window.location.pathname || '';
    const pageTitle = document.title || '';

    const pageLoadPayload = {
      event: 'page_load_time',
      page_load_time: metrics.pageLoadTime,
      page_path: pagePath,
      page_title: pageTitle,
    };
    const serverPayload = {
      event: 'server_response_time',
      server_response_time: metrics.serverResponseTime,
      page_path: pagePath,
      page_title: pageTitle,
    };

    void pushGtmEvent(pageLoadPayload);
    void pushGtmEvent(serverPayload);
    gtmDebugLog('page_load_time', pageLoadPayload);
    gtmDebugLog('server_response_time', serverPayload);
  }

  /**
   * Wait for window load, then retry briefly until loadEventEnd is populated.
   */
  function schedulePageTiming() {
    const attempt = () => {
      if (pageTimingFired) return true;
      if (readPageTimingMetrics()) {
        trackPageTiming();
        return true;
      }
      return false;
    };

    const runWithRetry = () => {
      if (attempt()) return;

      let tries = 0;
      const maxTries = 20;
      const timer = setInterval(() => {
        tries += 1;
        if (attempt() || tries >= maxTries) {
          clearInterval(timer);
        }
      }, 100);
    };

    if (document.readyState === 'complete') {
      runWithRetry();
    } else {
      window.addEventListener('load', runWithRetry, { once: true });
    }
  }

  /**
   * Push error_404 to dataLayer on 404 template page loads.
   * Source/medium is attributed in GA4 via the session (no custom params required).
   */
  function fire404ErrorEvent() {
    // @ts-ignore - GTM config is injected by Liquid template
    if (window.gtmConfig.pageType !== '404') return;

    const eventData = {
      event: 'error_404',
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer || '',
      page_title: document.title || '',
    };

    void pushGtmEvent(eventData);
    gtmDebugLog('error_404', eventData);
  }

  function firePageViewEvents() {
    // @ts-ignore - GTM config is injected by Liquid template
    const pageType = window.gtmConfig.pageType;

    gtmDebugLog('Page view', { pageType: pageType });

    switch (pageType) {
      case 'product':
        fireViewItemEvent();
        break;
      case 'collection':
        fireViewedCategoryEvent();
        fireViewItemListEvent();
        break;
      case 'cart':
        void fireViewCartEvent('page');
        break;
      case 'search':
        setupSearchResultTracking();
        break;
      case '404':
        fire404ErrorEvent();
        break;
      default:
        // Prefixbox/app search can still land on /search with an unexpected template name.
        if (isSearchResultsPage()) {
          setupSearchResultTracking();
        }
        break;
    }
  }

  /**
   * Initialize all GTM event tracking
   */
  function initGTMEvents() {
    // Setup cart event listener for add_to_cart tracking
    setupCartEventListener();

    // Track each visible mini-cart opening without duplicating cart-page views.
    setupViewCartDrawerTracking();

    // Cart / mini-cart GA4 cart_error (DOM scan + cart:error / cart:update)
    setupCartErrorCapture();

    // Setup variant change listener for PDP
    setupVariantChangeListener();

    // Arm search tracking for full page loads and later history navigations.
    setupGlobalSearchPageWatcher();

    // Fire initial page view events
    firePageViewEvents();

    // TWS-1048: page_load_time + server_response_time after window load
    schedulePageTiming();

    gtmDebugLog('GTM Events', { status: 'initialized' });
    console.info('[GTM] events initialized', {
      // @ts-ignore
      enabled: window.gtmConfig?.enabled,
      // @ts-ignore
      pageType: window.gtmConfig?.pageType,
      path: window.location.pathname,
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGTMEvents);
  } else {
    initGTMEvents();
  }

  // Expose tracking functions globally for use in other scripts
  // @ts-ignore - Global GTM tracking object
  window.gtmTrack = {
    // GA4 E-commerce events
    addToCart: trackAddToCart,
    removeFromCart: trackRemoveFromCart,
    selectItem: trackSelectItem,
    purchase: trackPurchase,

    // Additional events
    banner: trackBannerEvent,
    bid: trackBidEvent,

    // Manual page view triggers (for SPA navigation)
    viewItem: fireViewItemEvent,
    viewItemList: fireViewItemListEvent,
    viewCart: fireViewCartEvent,
    viewedCategory: fireViewedCategoryEvent,
    viewSearchResult: () => fireViewSearchResultEvent(true),

    // Variant change handler (for PDP)
    variantChange: handleVariantChange,

    // GA4 cart_error (structured payload; optional DOM rescan)
    trackCartError,
    cartError: captureCartErrorsForGA,

    // 404 page tracking (error_404 dataLayer event)
    track404: fire404ErrorEvent,

    // TWS-1048 page timing (also scheduled automatically on init)
    pageTiming: trackPageTiming,

    // Utility
    debug: gtmDebugLog,
  };
}
